'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Bell, Check, Info, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';
import { notifications } from '@/lib/data/notifications';
import { Notification } from '@/lib/data/types';

export default function NotificationWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    const getIcon = (type: Notification['type']) => {
        switch (type) {
            case 'success': return <CheckCircle className="w-4 h-4 text-white" />;
            case 'warning': return <AlertTriangle className="w-4 h-4 text-white" />;
            case 'error': return <XCircle className="w-4 h-4 text-white" />;
            case 'info':
            default: return <Info className="w-4 h-4 text-white" />;
        }
    };

    const getBgColor = (type: Notification['type']) => {
        switch (type) {
            case 'success': return 'bg-success';
            case 'warning': return 'bg-warning';
            case 'error': return 'bg-error';
            case 'info':
            default: return 'bg-info';
        }
    };

    const unreadCount = notifications.filter(n => !n.read).length;

    return (
        <div className="relative" ref={dropdownRef}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`w-10 h-10 flex items-center justify-center rounded-full border border-border-main transition-all cursor-pointer relative group
                ${isOpen ? 'bg-primary text-white border-primary' : 'bg-surface/60 text-muted hover:text-primary hover:bg-primary-soft/50'}`}
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 w-4 h-4 bg-error text-xs text-white flex items-center justify-center rounded-full  ring-2 ring-surface">{unreadCount}</span>
                )}
            </div>

            {isOpen && (
                <div className="absolute top-12 right-0 w-80 md:w-96 bg-surface border border-border-main rounded-2xl shadow-xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-200">
                    <div className="p-4 border-b border-border-main flex justify-between items-center bg-surface">
                        <h3 className="font-bold text-foreground">Notifikasi</h3>
                        <span className="text-[10px] bg-primary/10 text-primary px-2 py-1 rounded-full font-bold uppercase tracking-wider">
                            {unreadCount} Baru
                        </span>
                    </div>

                    <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
                        {notifications.length > 0 ? (
                            notifications.map((notification) => (
                                <div key={notification.id} className={`p-4 border-b border-border-main last:border-0 hover:bg-surface transition-colors cursor-pointer group ${!notification.read ? 'bg-primary/5' : ''}`}>
                                    <div className="flex gap-3">
                                        <div className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center shrink-0 shadow-sm ${getBgColor(notification.type)}`}>
                                            {getIcon(notification.type)}
                                        </div>
                                        <div>
                                            <h4 className={`text-sm font-bold mb-0.5 ${!notification.read ? 'text-foreground' : 'text-foreground/70'}`}>
                                                {notification.title}
                                            </h4>
                                            <p className="text-xs text-secondary leading-relaxed mb-2">
                                                {notification.message}
                                            </p>
                                            <span className="text-[10px] font-bold text-muted uppercase tracking-wider block">
                                                {notification.time}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-8 text-center text-muted">
                                <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                <p className="text-sm">Tidak ada notifikasi</p>
                            </div>
                        )}
                    </div>

                    <div className="p-3 bg-surface border-t border-border-main text-center">
                        <button className="text-xs font-bold text-primary hover:text-primary-dark transition-colors uppercase tracking-wider">
                            Tandai semua dibaca
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
